import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Calendar,
  CheckCircle2,
  ShieldCheck,
  ArrowRight,
  Video,
  Copy,
  ArrowLeft,
  Mail,
} from 'lucide-react';
import PortalLayout from '../components/portal/PortalLayout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { NEXT_MEETING, CIRCLE_PRICE } from '../data/portalData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HEARD_OPTIONS = [
  'Instagram',
  'Facebook',
  'A brother invited me',
  'Podcast / interview',
  'The Reset retreat',
  'Google search',
  'Other',
];

// -------- Registration form --------
export function MonthlyCircleRegister() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [howHeard, setHowHeard] = useState('');
  const [interested, setInterested] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    if (interested === null) {
      toast.error('Let us know if you\u2019re interested in Reset 2027');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/monthly-circle/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          how_heard: howHeard || null,
          interested_in_2027: interested,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Registration failed');
      }
      const data = await res.json();
      // Redirect to Stripe checkout
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <PortalLayout>
      <Toaster position="top-center" richColors />
      <div className="max-w-3xl mx-auto px-5 py-12">
        <Link
          to="/portal/monthly-meeting"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-[#D4AF37] font-manrope text-sm transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Monthly Meeting
        </Link>

        <header className="mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mb-5">
            <Calendar className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-xs font-bold mb-4">
            Register &middot; Monthly Circle
          </p>
          <h1 className="font-cormorant text-4xl md:text-6xl font-medium text-white leading-[1.05] mb-4">
            One check-in <span className="italic text-[#D4AF37]">a month.</span>
          </h1>
          <p className="text-zinc-400 font-manrope text-base md:text-lg max-w-2xl leading-relaxed">
            {NEXT_MEETING.date} &middot; {NEXT_MEETING.time}. ${CIRCLE_PRICE} secures your seat and gets
            the Google Meet link sent straight to your inbox.
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="bg-[#111111] border border-white/10 rounded-2xl p-6 md:p-8 space-y-6"
          data-testid="monthly-register-form"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 font-manrope text-xs uppercase tracking-widest block mb-2">
                Full name
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12"
                data-testid="monthly-name-input"
                required
              />
            </div>
            <div>
              <label className="text-zinc-400 font-manrope text-xs uppercase tracking-widest block mb-2">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12"
                data-testid="monthly-email-input"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-zinc-400 font-manrope text-xs uppercase tracking-widest block mb-2">
              How did you hear about The Reset Brotherhood?
            </label>
            <div className="flex flex-wrap gap-2" data-testid="monthly-heard-pills">
              {HEARD_OPTIONS.map((opt) => {
                const selected = howHeard === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setHowHeard(opt)}
                    className={`px-4 py-2 rounded-full font-manrope text-sm border transition-all ${
                      selected
                        ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]'
                        : 'bg-white/5 text-zinc-300 border-white/10 hover:border-[#D4AF37]/40 hover:text-white'
                    }`}
                    data-testid={`heard-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-zinc-400 font-manrope text-xs uppercase tracking-widest block mb-2">
              Interested in attending The Reset 2027?
            </label>
            <div className="flex gap-2" data-testid="monthly-interested-yesno">
              {['Yes', 'No'].map((label) => {
                const val = label === 'Yes';
                const isSelected = interested === val;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setInterested(val)}
                    className={`min-w-[80px] px-5 py-2.5 rounded-full font-manrope text-sm font-medium border transition-all ${
                      isSelected
                        ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]'
                        : 'bg-white/5 text-zinc-300 border-white/10 hover:border-[#D4AF37]/40 hover:text-white'
                    }`}
                    data-testid={`interested-${label.toLowerCase()}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2 border-t border-white/5">
            <p className="text-zinc-500 font-manrope text-xs flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
              Secure Stripe checkout &middot; Google Meet link emailed after payment
            </p>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#D4AF37] text-[#0A0A0A] px-8 h-12 rounded-full font-manrope font-semibold hover:bg-amber-200 disabled:opacity-50 group"
              data-testid="monthly-submit-btn"
            >
              {submitting ? 'Redirecting…' : (
                <>
                  Continue to payment &middot; ${CIRCLE_PRICE}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </PortalLayout>
  );
}

// -------- Post-payment success --------
export function MonthlyCircleSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [meetUrl, setMeetUrl] = useState('');
  const [reg, setReg] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (!sessionId) {
        setStatus('missing');
        return;
      }
      try {
        const res = await fetch(`${API}/monthly-circle/confirm/${sessionId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setMeetUrl(data.meet_url);
          setReg(data.registration);
          setStatus('paid');
          return;
        }
        attempts += 1;
        if (attempts < 15) {
          setTimeout(poll, 2000);
        } else {
          setStatus('timeout');
        }
      } catch (err) {
        if (attempts < 15 && !cancelled) {
          attempts += 1;
          setTimeout(poll, 2000);
        } else if (!cancelled) {
          setStatus('error');
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const copyMeet = () => {
    navigator.clipboard.writeText(meetUrl);
    toast.success('Google Meet link copied');
  };

  return (
    <PortalLayout>
      <Toaster position="top-center" richColors />
      <div className="max-w-2xl mx-auto px-5 py-16 text-center">
        {status === 'checking' && (
          <div data-testid="monthly-success-checking">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6 animate-pulse">
              <ShieldCheck className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="font-cormorant text-4xl font-medium text-white mb-3">
              Confirming your payment…
            </h1>
            <p className="text-zinc-400 font-manrope">
              This usually takes a few seconds. Please stay on this page.
            </p>
          </div>
        )}
        {status === 'paid' && (
          <div data-testid="monthly-success-paid">
            <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-xs font-bold mb-3">
              Payment confirmed
            </p>
            <h1 className="font-cormorant text-4xl md:text-5xl font-medium text-white mb-3">
              You&rsquo;re in{reg?.name ? `, ${reg.name.split(' ')[0]}` : ''}.
            </h1>
            <p className="text-zinc-400 font-manrope text-lg mb-8">
              Your seat at the Monthly Brotherhood Circle is secured. Save the meeting link below.
            </p>

            {/* Email confirmation banner */}
            <div
              className="rounded-2xl bg-[#0f2a1a] border border-emerald-500/30 p-5 text-left mb-6 flex items-start gap-3"
              data-testid="monthly-success-email-sent"
            >
              <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-emerald-300" />
              </div>
              <div>
                <p className="text-emerald-200 font-manrope text-sm font-semibold mb-0.5">
                  Google Meet invite &amp; calendar file sent
                </p>
                <p className="text-emerald-100/70 font-manrope text-xs leading-relaxed">
                  {reg?.email ? (
                    <>
                      Check <span className="text-white">{reg.email}</span> for the
                      Google Meet link + <span className="text-white">reset-circle.ics</span>{' '}
                      calendar invite. Tap the .ics to add the Circle to Google
                      Calendar, Apple Calendar, or Outlook.
                    </>
                  ) : (
                    <>
                      We just emailed you the Google Meet link and a calendar (.ics)
                      invite. Check your inbox.
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="rounded-2xl bg-[#111111] border border-[#D4AF37]/30 p-6 text-left mb-6">
              <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest font-bold mb-2">
                Google Meet Link
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
                <a
                  href={meetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white font-manrope text-sm break-all hover:text-[#D4AF37]"
                  data-testid="meet-link"
                >
                  {meetUrl}
                </a>
                <button
                  onClick={copyMeet}
                  className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-[#D4AF37] font-manrope text-xs"
                  data-testid="copy-meet-btn"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
              <p className="text-zinc-500 font-manrope text-xs mt-3">
                We&rsquo;ve also emailed this to you. Add it to your calendar &middot; {NEXT_MEETING.date} &middot; {NEXT_MEETING.time}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={meetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 rounded-full font-manrope font-semibold hover:bg-amber-200 transition-colors"
              >
                <Video className="w-4 h-4" /> Open Google Meet
              </a>
              <button
                onClick={() => navigate('/portal')}
                className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-full font-manrope font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                Back to Portal
              </button>
            </div>
          </div>
        )}
        {(status === 'timeout' || status === 'error') && (
          <div data-testid="monthly-success-error">
            <h1 className="font-cormorant text-3xl font-medium text-white mb-3">
              Still confirming your payment.
            </h1>
            <p className="text-zinc-400 font-manrope">
              Refresh in a moment. If the issue persists, email{' '}
              <a href="mailto:nalvanzo@gmail.com" className="text-[#D4AF37] hover:underline">
                nalvanzo@gmail.com
              </a>{' '}
              with your session ID: <code className="text-zinc-500">{sessionId}</code>
            </p>
          </div>
        )}
        {status === 'missing' && (
          <div>
            <h1 className="font-cormorant text-3xl font-medium text-white mb-3">
              Missing session.
            </h1>
            <Link
              to="/portal/monthly-meeting/register"
              className="text-[#D4AF37] hover:underline font-manrope"
            >
              Go back and register
            </Link>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
